import { useCallback, useEffect, useState } from 'react';
import type {
  CelebrationCue,
  DailyPageView,
  IdeaCountsView,
  IdeaView,
  IsoDate,
  NextUpView,
  NoteView,
  ObjectiveSummaryView,
  PriorityView,
  Rating,
  RewardConfig,
  TrailingAnalyticsView,
} from '@contracts';
import { DEFAULT_REWARD_CONFIG } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { OfflineChip } from '@ui/shared/primitives/OfflineChip';
import { localIsoDate } from '@ui/shared/format/date';
import { XPToast } from '@ui/shared/celebrations/XPToast';
import { LevelUpMoment } from '@ui/shared/celebrations/LevelUpMoment';
import { BadgeEarnSheet } from '@ui/shared/celebrations/BadgeEarnSheet';
import { useCelebrationQueue } from '@ui/shared/celebrations/useCelebrationQueue';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { addDaysToDate } from '@domain/habits/week';
import { getStoredDayHeadline, setStoredDayHeadline } from '@ui/shared/settings/dayHeadline';

import { TodayHeaderDesktopV2 } from './TodayHeaderDesktopV2';
import { SentenceOfDayCard } from './SentenceOfDayCard';
import { MorningCheckInCard } from './MorningCheckInCard';
import { GratitudeCard } from './GratitudeCard';
import { PrioritiesCard } from './PrioritiesCard';
import { BrainDumpCard } from './BrainDumpCard';
import { IdeasCard } from './IdeasCard';
import { FavoriteThingCard } from './FavoriteThingCard';
import { NextUpTomorrowCard } from './NextUpTomorrowCard';
import { TrailingAnalyticsCards } from './TrailingAnalyticsCards';

/**
 * Today desktop v2 (docs/specs/phase-6-today-desktop-v2.md,
 * design/screens/today-desktop-v2.md) — REPLACES the pre-Phase-6 desktop
 * Today screen in full. Six distinct row shapes at a 1600px shell (SS1):
 * header, Row A (Sentence of the day, full width), Row B (Morning check-in +
 * Grateful), Row C (Priorities + Brain dump), Row D (Ideas + Favorite thing),
 * Row E (Next up · tomorrow, full width), Row F (three analytics cards).
 *
 * No footer/nav here by design (same reasoning as the pre-Phase-6 screen this
 * replaces): `DesktopShell`'s rail + `SidebarIdentity` already cover every
 * destination and the ambient level/XP display.
 */
export function TodayViewDesktop({
  onOpenObjective,
  onCreateObjective: _onCreateObjective,
  onOpenRewards: _onOpenRewards,
  onOpenHabits: _onOpenHabits,
  onOpenWeek: _onOpenWeek,
  onOpenObjectives: _onOpenObjectives,
  onOpenHistory,
  today: todayOverride,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly onCreateObjective: () => void;
  readonly onOpenRewards: () => void;
  readonly onOpenHabits: () => void;
  readonly onOpenWeek: () => void;
  readonly onOpenObjectives: () => void;
  readonly onOpenHistory: () => void;
  /** Test override, mirroring the phone `TodayView`'s own `today` prop. */
  readonly today?: IsoDate;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  // Stable "what is real today" value, distinct from `date` (the currently
  // VIEWED date, which pages around as the user navigates). Threaded down
  // to TodayHeaderDesktopV2 as `realToday` instead of letting it recompute
  // its own `new Date()` internally — that independent computation was a
  // real, reported bug class (the same real-clock-vs-fixture-clock drift
  // already tracked for ObjectiveFormScreen.desktop.tsx): a component that
  // silently reads the system clock instead of an injected value drifts
  // out of sync with whatever "today" the rest of a test/session is using.
  const realToday: IsoDate = todayOverride ?? (localIsoDate() as IsoDate);
  const [date, setDate] = useState<IsoDate>(realToday);
  const [page, setPage] = useState<DailyPageView | null>(null);
  const [priorities, setPriorities] = useState<readonly PriorityView[]>([]);
  const [notes, setNotes] = useState<readonly NoteView[]>([]);
  const [ideas, setIdeas] = useState<readonly IdeaView[]>([]);
  const [ideaCounts, setIdeaCounts] = useState<IdeaCountsView>({ createdToday: 0, createdThisMonth: 0 });
  const [objectives, setObjectives] = useState<readonly ObjectiveSummaryView[]>([]);
  const [nextUp, setNextUp] = useState<NextUpView | null>(null);
  const [analytics, setAnalytics] = useState<TrailingAnalyticsView | null>(null);
  const [offline, setOffline] = useState(false);
  const [rewardConfig, setRewardConfig] = useState<RewardConfig>(DEFAULT_REWARD_CONFIG);
  const [headline, setHeadline] = useState('');

  const celebration = useCelebrationQueue({
    resolveIdentity: (level) =>
      resolveLevelIdentity({ xpTotal: 0, level, xpIntoCurrentLevel: 0, xpRequiredForNextLevel: 100, streakDays: 0 }, rewardConfig)
        .identity,
  });

  const tomorrow = addDaysToDate(date, 1) as IsoDate;

  const load = useCallback(async () => {
    const [dailyPage, priorityList, noteList, ideaList, counts, objectiveList, next, trailingAnalytics, config] =
      await Promise.all([
        api.getDailyPage(date),
        api.listPriorities(date),
        api.listNotes(),
        api.listIdeas(),
        api.getIdeaCounts(date),
        api.listObjectives({ includeArchived: false }),
        api.getNextUp(tomorrow),
        api.getTrailingAnalytics(date),
        api.getRewardConfig(),
      ]);
    setPage(dailyPage);
    setPriorities(priorityList);
    setNotes(noteList);
    setIdeas(ideaList);
    setIdeaCounts(counts);
    setObjectives(objectiveList);
    setNextUp(next);
    setAnalytics(trailingAnalytics);
    setRewardConfig(config);
    setHeadline(getStoredDayHeadline(date));
  }, [api, date, tomorrow]);

  useEffect(() => {
    void load();
  }, [load, version]);

  useEffect(() => {
    const status = api.getSyncStatus();
    setOffline(status.state === 'offline_queued');
  }, [api, version]);

  const handleCue = useCallback(
    (cue: CelebrationCue, sourceLabel: string) => {
      if (cue.xpAwarded <= 0 && !cue.levelUp && cue.badgesEarned.length === 0) return;
      void api.getRewardSnapshot().then((snapshot) => {
        celebration.enqueue(cue, sourceLabel, snapshot.xpTotal);
      });
    },
    [api, celebration],
  );

  function setMoodValue(next: Rating) {
    if (page && page.mood === next && page.moodEventId) {
      void api.revertLogEntry(page.moodEventId).then(() => void load());
    } else {
      void api.setMood(date, next).then(() => void load());
    }
  }

  function setEnergyValue(next: Rating) {
    if (page && page.energy === next && page.energyEventId) {
      void api.revertLogEntry(page.energyEventId).then(() => void load());
    } else {
      void api.setEnergy(date, next).then(() => void load());
    }
  }

  function setHeadlineValue(next: string) {
    setStoredDayHeadline(date, next);
    setHeadline(next);
  }

  async function closeDay(): Promise<CelebrationCue> {
    const cue = await api.closeDay(date);
    handleCue(cue, 'Day closed');
    await load();
    return cue;
  }

  const celebrationOverlays = (
    <>
      {celebration.xpToast && (
        <XPToast amount={celebration.xpToast.amount} sourceLabel={celebration.xpToast.sourceLabel} onDismiss={celebration.dismissToast} />
      )}
      {celebration.levelUp && (
        <LevelUpMoment
          level={celebration.levelUp.level}
          identity={celebration.levelUp.identity}
          xpTotal={celebration.levelUp.xpTotal}
          onDismiss={celebration.dismissLevelUp}
        />
      )}
      {celebration.visibleBadgeId && (
        <BadgeEarnSheetLoader badgeId={celebration.visibleBadgeId} onDismiss={celebration.dismissNextBadge} />
      )}
    </>
  );

  if (!page || !nextUp || !analytics) {
    return (
      <main aria-label="Today" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}>
        <SkeletonBlock height={80} />
        <div style={{ marginTop: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          <SkeletonBlock height={100} />
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 420px', gap: 'var(--space-6)' }}>
            <SkeletonBlock height={240} />
            <SkeletonBlock height={240} />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      aria-label="Today"
      style={{
        padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)',
        maxWidth: '1600px',
        margin: '0 auto',
      }}
    >
      <TodayHeaderDesktopV2
        date={date}
        realToday={realToday}
        headline={headline}
        dayClosed={page.dayClosed}
        onSetHeadline={setHeadlineValue}
        onPageDate={setDate}
        onCloseDay={closeDay}
        onOpenHistory={onOpenHistory}
      />

      {offline && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <OfflineChip />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        <SentenceOfDayCard />

        <Row>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <MorningCheckInCard
              date={date}
              mood={page.mood}
              energy={page.energy}
              note={page.note}
              sleepHours={page.sleepHours}
              onSetMood={setMoodValue}
              onSetEnergy={setEnergyValue}
              onSetNote={(next) => void api.setNote(date, next).then(() => void load())}
              onSetSleepHours={(hours) => void api.setSleepHours(date, hours).then(() => void load())}
            />
          </div>
          <GratitudeCard
            gratitude={page.gratitude}
            onSetSlot={(slot, text) => void api.setGratitudeSlot(date, slot, text).then(() => void load())}
          />
        </Row>

        <Row>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <PrioritiesCard
              date={date}
              priorities={priorities}
              objectives={objectives}
              onChanged={() => void load()}
              onOpenObjective={onOpenObjective}
            />
          </div>
          <BrainDumpCard
            notes={notes}
            objectives={objectives}
            priorityCount={priorities.length}
            onChanged={() => void load()}
          />
        </Row>

        <Row>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <IdeasCard ideas={ideas} counts={ideaCounts} objectives={objectives} onChanged={() => void load()} />
          </div>
          <FavoriteThingCard
            favoriteMoment={page.favoriteMoment}
            onSave={(input) => void api.setFavoriteMoment(date, input).then(() => void load())}
          />
        </Row>

        <NextUpTomorrowCard tomorrowDate={tomorrow} nextUp={nextUp} onOpenTomorrow={() => setDate(tomorrow)} />

        <TrailingAnalyticsCards analytics={analytics} />
      </div>

      {celebrationOverlays}
    </main>
  );
}

/** The Row B/C/D two-column layout: main column (`minmax(0, 1fr)`) + a
 *  420px fixed side card, `space.6` gap (design/screens/today-desktop-v2.md
 *  §1). */
function Row({ children }: { readonly children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'flex-start' }}>{children}</div>;
}

/**
 * Resolves a BadgeId to its BadgeView before rendering the earn sheet —
 * identical to the pre-Phase-6 desktop Today's own loader.
 */
function BadgeEarnSheetLoader({ badgeId, onDismiss }: { readonly badgeId: string; readonly onDismiss: () => void }) {
  const api = useDomainApi();
  const [badge, setBadge] = useState<Awaited<ReturnType<typeof api.listBadges>>[number] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listBadges().then((badges) => {
      if (cancelled) return;
      setBadge(badges.find((b) => b.badgeId === badgeId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [api, badgeId]);

  if (!badge) return null;
  return <BadgeEarnSheet badge={badge} onDismiss={onDismiss} />;
}
